import { z } from 'zod';
import { PROVIDER_KINDS, PROVINCES } from './enums.js';
import { PublicMedia } from './media.js';
import { Cuid2, DiscountPercent, IsoDateTime, Slug } from './primitives.js';
/**
 * What the public sees of a provider: the feed, the browse grid, the profile
 * page and the directory under /providers.
 *
 * Only APPROVED providers who are not on holiday appear here (D13), so
 * nothing in these shapes carries a status. A screen that could render a
 * rejected provider is a screen that one day will.
 */
/** An area as a member picks it, with the slug the directory URL uses. */
export const PublicAreaRef = z.object({
    id: Cuid2,
    slug: Slug,
    name: z.string(),
    city: z.string(),
    province: z.enum(PROVINCES),
});
/** A trade category, with the slug in /providers/[category]/[area]. */
export const PublicCategoryRef = z.object({ id: Cuid2, slug: Slug, name: z.string() });
/**
 * The provider's own rating. Null until anyone has reviewed them, which is
 * not the same as zero: a new provider has no score, they do not have a bad
 * one. Screens must show "No reviews yet", never "0".
 */
export const ProviderRating = z.object({
    average: z.number().min(1).max(5),
    count: z.int().nonnegative(),
});
/**
 * One provider, as a feed post or a grid card shows them (DESIGN.md,
 * "Provider card" and "Feed").
 *
 * `displayName` is resolved by the API rather than sent as legalName plus
 * tradingName, so every surface shows the same name and no screen has to
 * remember the rule.
 */
export const ProviderSummary = z.object({
    id: Cuid2,
    slug: Slug,
    displayName: z.string(),
    kind: z.enum(PROVIDER_KINDS),
    /** Always true here: only approved providers are public. Kept so the badge has a field to read. */
    verified: z.literal(true),
    /** Where they are based. Null for a provider who only lists service areas. */
    basedInArea: PublicAreaRef.nullable(),
    categories: z.array(PublicCategoryRef),
    /** The most prominent number on the card (DESIGN.md). Null when they offer no percentage. */
    discountPercent: DiscountPercent.nullable(),
    /** A discount that is not a percentage, e.g. "Free call-out for members". */
    discountText: z.string().nullable(),
    rating: ProviderRating.nullable(),
    responseTimeMinutes: z.int().positive().nullable(),
    callOutFee: z.boolean().nullable(),
    emergencyAvailable: z.boolean(),
    /** E.164. The Call action is primary, so the number travels with the card. */
    phone: z.string(),
    avatar: PublicMedia.nullable(),
    /** Up to 6, for the 4:5 carousel. Empty only for a provider approved before D32. */
    photos: z.array(PublicMedia).max(6),
});
/** The provider's public page: everything in the card, plus the long-form detail. */
export const ProviderPublicProfile = ProviderSummary.extend({
    overview: z.string().nullable(),
    services: z.array(z.string()),
    paymentMethods: z.array(z.string()),
    serviceAreas: z.array(PublicAreaRef),
    /** Attorneys only (requirements.md). */
    hourlyRateCents: z.int().nonnegative().nullable(),
    /** YYYY-MM-DD, when the business started trading. */
    activeSince: z.string().nullable(),
    /** The full set, not the six the card carries. */
    photos: z.array(PublicMedia),
    /** When MyHome+ approved them, for "On MyHome+ since". */
    approvedAt: IsoDateTime.nullable(),
});
/**
 * A cursor-paged list.
 *
 * Cursors, not page numbers: the feed and the directory are ordered by things
 * that change while someone is scrolling, and offset paging duplicates and
 * skips rows when they do. `nextCursor` is null on the last page.
 */
export function Page(item) {
    return z.object({
        items: z.array(item),
        nextCursor: z.string().min(1).nullable(),
    });
}
export const ProviderSummaryPage = Page(ProviderSummary);
//# sourceMappingURL=directory.js.map